import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { Input } from '@/components/form/Input';
import { Option, Select } from '@/components/form/Select';
import { useModifyAccount } from '@/hooks/useAccounts';
import { generateSeverityOptions } from '@/hooks/useIntegration';
import { Account } from '@/types';

interface EmailNotificationEditFormProps {
  /** The email notification account to edit */
  integration: Account;
  /** Callback when modal should close */
  onClose?: () => void;
}

/**
 * Email notification edit form
 *
 * Modal form for editing email notification settings including:
 * - Email address
 * - Severity threshold for notifications
 *
 * Uses useModifyAccount('update') to update the email notification.
 * The 'update' action sends a PUT request with the integration key,
 * which updates the existing record instead of creating a new one.
 *
 * Used in:
 * - NotificationSettingsCards via Modal
 */
export const EmailNotificationEditForm: React.FC<
  EmailNotificationEditFormProps
> = ({ integration, onClose }) => {
  // Get current values from integration
  // Email is stored in the value field
  const currentEmail = integration.value || '';
  // Severity is stored in config.severities (via AccountMetadata index signature)
  // or could be in settings, depending on how it was originally set
  const currentSeverity =
    (integration.config?.severities as string) ||
    (integration.settings as { severities?: string } | undefined)?.severities ||
    'MHC';

  const [email, setEmail] = useState(currentEmail);
  const [severity, setSeverity] = useState(currentSeverity);

  const { mutateAsync: updateEmail, status } = useModifyAccount('update');

  const isLoading = status === 'pending';

  // Validation: email must be valid format
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const hasChanged = email !== currentEmail || severity !== currentSeverity;

  const severityOptions: Option[] = generateSeverityOptions();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEmailValid || !hasChanged) {
      return;
    }

    try {
      await updateEmail({
        username: 'email',
        value: email,
        key: integration.key,
        config: {}, // Required by LinkAccount type, but not used by 'update' action
        settings: {
          severityThreshold: severity,
        },
        silent: true, // Suppress hook toast - component shows custom message
      });

      toast.success('Email notification updated', {
        description: 'Your email notification settings have been saved.',
      });

      onClose?.();
    } catch (error) {
      // Error toast is handled by useModifyAccount
      console.error('Failed to update email notification:', error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6"
      data-testid="email-notification-edit-form"
      role="form"
    >
      {/* Email Address Input */}
      <div className="flex flex-col gap-3">
        <Input
          name="email"
          label="Email Address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="example@companydomain.com"
          required
          disabled={isLoading}
          error={
            email && !isEmailValid
              ? 'Please enter a valid email address'
              : undefined
          }
          data-testid="email-notification-email-input"
        />
      </div>

      {/* Severity Dropdown */}
      <div className="flex flex-col gap-3">
        <label className="text-sm font-semibold" htmlFor="severity">
          Severity Threshold
        </label>
        <Select
          name="severity"
          value={severity}
          onChange={e => setSeverity(e.target.value)}
          options={severityOptions}
          disabled={isLoading}
          data-testid="email-notification-severity-select"
        />
        <p className="text-chariot-text-secondary text-xs">
          Select the minimum risk severity level at which Chariot should send
          notifications.
        </p>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-2 pt-4 pb-1">
        <Button
          onClick={onClose}
          label="Cancel"
          styleType="text"
          className="bg-chariot-background-primary px-4"
          size="md"
          disabled={isLoading}
          data-testid="email-notification-cancel-button"
        />
        <Button
          type="submit"
          label={isLoading ? 'Saving...' : 'Save'}
          styleType="primary"
          className="ml-2 min-w-24 px-4"
          size="md"
          disabled={isLoading || !hasChanged || !isEmailValid}
          data-testid="email-notification-save-button"
        />
      </div>
    </form>
  );
};
